import { create } from 'zustand';
import type { RebalanceData } from '../api/types';

/**
 * Recent basket REBALANCES, scoped per portfolioId. Fed by the `rebalances` WS
 * channel: a `rebalance-snapshot` on subscribe (recent history) and a
 * `rebalance` push per new rebalance. The portfolios page reads the list for
 * the selected wallet to render a pretty rebalance timeline.
 *
 * Newest-first. Bounded per portfolio so a long-lived session can't grow it
 * without limit. This is a convenience view, not a durable record.
 */

// Matches the gateway ring (DataCache.MAX_REBALANCES_PER_PORTFOLIO) so a
// snapshot-on-subscribe isn't truncated below what the server holds.
const MAX_PER_PORTFOLIO = 40;

const EMPTY: RebalanceData[] = [];

type State = {
  byPortfolio: Record<string, RebalanceData[]>;
  /** Replace the list for a portfolio (snapshot-on-subscribe). */
  setSnapshot: (rebalances: RebalanceData[]) => void;
  /** Prepend a single new rebalance, deduping on (strategyId, rebalancedAt). */
  add: (rebalance: RebalanceData) => void;
};

export const useRebalancesStore = create<State>((set) => ({
  byPortfolio: {},

  setSnapshot: (rebalances) =>
    set((state) => {
      if (rebalances.length === 0) return state;
      const portfolioId = rebalances[0]!.portfolioId;
      const sorted = [...rebalances]
        .sort((a, b) => b.rebalancedAt - a.rebalancedAt)
        .slice(0, MAX_PER_PORTFOLIO);
      return { byPortfolio: { ...state.byPortfolio, [portfolioId]: sorted } };
    }),

  add: (rebalance) =>
    set((state) => {
      const list = state.byPortfolio[rebalance.portfolioId] ?? [];
      // Dedup: the same (strategyId, tick) can arrive twice on reconnect.
      const exists = list.some(
        (r) =>
          r.strategyId === rebalance.strategyId &&
          r.rebalancedAt === rebalance.rebalancedAt,
      );
      if (exists) return state;

      // Collapse a run of consecutive regime-only ticks in the same gate state
      // into the head row (mirrors the gateway ring's own collapse). The live
      // push carries a fresh `rebalancedAt` each ~5min cycle, so without this the
      // quiet status ticks would stack up as distinct rows and bury the rare real
      // rotations. A real rotation (regimeOnly !== true) always prepends.
      const head = list[0];
      if (
        rebalance.regimeOnly === true &&
        head?.regimeOnly === true &&
        head.strategyId === rebalance.strategyId &&
        head.regimeClosed === rebalance.regimeClosed
      ) {
        const merged: RebalanceData = {
          ...rebalance,
          firstSeenAt: head.firstSeenAt ?? head.rebalancedAt,
          repeatCount: (head.repeatCount ?? 1) + 1,
        };
        return {
          byPortfolio: {
            ...state.byPortfolio,
            [rebalance.portfolioId]: [merged, ...list.slice(1)],
          },
        };
      }

      const next = [rebalance, ...list].slice(0, MAX_PER_PORTFOLIO);
      return {
        byPortfolio: { ...state.byPortfolio, [rebalance.portfolioId]: next },
      };
    }),
}));

/** Stable selector: the rebalance list for one portfolio (newest-first). */
export function selectRebalances(
  portfolioId: string | null | undefined,
): (s: State) => RebalanceData[] {
  return (s) => (portfolioId ? s.byPortfolio[portfolioId] ?? EMPTY : EMPTY);
}
